import { ft } from '../core/units.js';
import { EVENT } from './events.js';
import { BLOCKADES } from '../data/blockades.js';

/**
 * Animals in the road, and a shuttle due along it.
 *
 * Posting one puts the group out and starts the clock, and that is all. How
 * they come off the road is `wildlife.js`: they stand where they are, and they
 * go where food goes. This file watches, and keeps the score: clear when the
 * shuttle comes, still there, or one of them hurt.
 *
 * Hurt is anything that arrives at one of them hard: a cart driven into it at
 * more than a walk, or something that lands on it. Both are the same as they
 * are anywhere, an impact, and neither is a rule written for one animal: it is a
 * body where a thing hit.
 */

/** Metres per second. A cart touching one slower than this has nudged it. */
const NUDGE = 1.2;

/** Feet. Something landing this close to one has landed on it. */
const STRUCK = 2.5;

const clockOf = (clock) => clock.day * 1440 + clock.minutes;

export function createBlockades({ wildlife, wheeled, level, clock, bus, standing, hud, rows = BLOCKADES }) {
  let active = null;
  let records = [];

  const rowOf = (id) => rows.find((row) => row.id === id) ?? null;
  const herd = () => (active ? wildlife.creatures.filter((creature) => creature.group === active.group) : []);
  const roomOf = (body) => level.roomAt(body.position.x, body.position.z)?.id ?? null;
  const reachOf = (creature) => ft(creature.definition.radius);

  function close(outcome) {
    const row = rowOf(active.id);
    records.push({ id: active.id, day: active.day, outcome, at: clock.minutes, onRoad: active.onRoad ?? 0 });
    const changes = row[outcome === 'clear' ? 'award' : outcome] ?? {};
    const [line, reason] = row.says[outcome];
    standing?.applyAll(changes, { reason });
    hud?.say(line, { tone: outcome === 'clear' ? 'good' : 'bad', seconds: 4 });
    // And they go back where they came from, whatever happened.
    wildlife.dismiss(active.group);
    active = null;
  }

  // Something thrown, landing on one.
  bus.on(EVENT.IMPACT, (event) => {
    if (!active || event.actor !== 'player' || !event.position) return;
    if (event.target?.type === 'body') return;
    for (const creature of herd()) {
      const gap = Math.hypot(event.position.x - creature.position.x, event.position.z - creature.position.z);
      if (gap < ft(STRUCK) + reachOf(creature) * 0.5 && (event.energy ?? 0) > 2) active.hurt = true;
    }
  });

  return {
    rows,

    /** Something is in the road. From a beat, when it is played. */
    post(id) {
      const row = rowOf(id);
      if (!row || active) return false;
      const out = wildlife.spawn(row.animal.id, { x: ft(row.animal.at[0]), y: 0, z: ft(row.animal.at[1]) }, { day: clock.day }) ?? [];
      if (!out.length) return false;
      active = { id, day: clock.day, from: clockOf(clock), group: out[0].group, hurt: false, onRoad: out.length };
      return true;
    },

    get active() {
      return active ? { ...active } : null;
    },

    get records() {
      return records.map((record) => ({ ...record }));
    },

    /** Where they are, for the objective and the map: the middle of the group, in metres. */
    get where() {
      const all = herd();
      if (!all.length) return null;
      return {
        x: all.reduce((sum, c) => sum + c.position.x, 0) / all.length,
        z: all.reduce((sum, c) => sum + c.position.z, 0) / all.length,
      };
    },

    works: () => false,

    update() {
      if (!active) return;
      const row = rowOf(active.id);
      const all = herd();
      // Somebody took them away (a new week, a script clearing the desert).
      if (!all.length) {
        active = null;
        return;
      }
      // A cart driven into one.
      for (const cart of wheeled.all ?? []) {
        const speed = Math.hypot(cart.velocity.x, cart.velocity.z);
        if (speed <= NUDGE) continue;
        for (const creature of all) {
          const gap = Math.hypot(cart.position.x - creature.position.x, cart.position.z - creature.position.z);
          if (gap < wheeled.radius(cart) + reachOf(creature) * 0.6) {
            active.hurt = true;
            bus.emit(EVENT.IMPACT, {
              actor: 'player',
              kind: cart.kind,
              target: { type: 'creature', id: creature.id },
              position: { ...creature.position },
              energy: 0.5 * cart.definition.mass * speed * speed * 0.02,
              speed,
              broke: false,
            });
          }
        }
      }
      if (active.hurt) return close('hurt');
      active.onRoad = all.filter((creature) => row.road.includes(roomOf(creature))).length;
      if (!active.onRoad) return close('clear');
      if (clockOf(clock) - active.from >= row.due) return close('blocked');
    },

    toJSON: () => ({ records }),
    load(state) {
      // Where the animals were is not saved, so neither is the job.
      active = null;
      records = (state?.records ?? []).map((record) => ({ ...record }));
    },
    clear() {
      active = null;
      records = [];
    },
  };
}
